import { useEffect } from 'react';
import type { Location } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { getMe } from '../api/get-me';
import { token } from '../lib/token';
import { notFoundRoute, whiteList } from '../router/routes';
import { generateRoutes } from '../router/utils';
import { useRouterStore } from '../store';
import useAuthStore from '../store/auth/auth.store';

const useRouterGuard = (to: Location) => {
  const { pathname } = useLocation();
  const {
    userInfo,
    userPermissions,
    setUserInfo,
    setRetrying,
    setUserPermissions,
    setPermissionVersion,
  } = useAuthStore();
  const navigate = useNavigate();
  const { addRoutes } = useRouterStore();

  useEffect(() => {
    let isCancelled = false;
    const permission = async () => {
      if (whiteList.includes(pathname)) return;

      if (!token.get()) {
        setUserInfo(null);
        setUserPermissions([]);
        setPermissionVersion(null);
        return navigate('/login', { replace: true, state: { to } });
      }

      // Only skip if we have both user info AND permissions loaded
      if (userInfo?.id && userPermissions.length > 0) {
        return;
      }
      try {
        setRetrying(true);
        const response = await getMe();

        setRetrying(false);
        if (isCancelled) return;

        if (response?.data) {
          // Generate routes based on permissions (admin gets all routes)
          const routes = generateRoutes(response.data.permissions || []);

          setUserInfo({
            id: response?.data.id,
            name: response?.data.name,
            photo: response?.data.photo,
          });

          // Set user permissions from the /me response
          if (response.data.permissions) {
            setUserPermissions(response.data.permissions);
          }

          // Set permission version for change detection
          if (response.data.permission_version) {
            setPermissionVersion(response.data.permission_version);
          }

          addRoutes([...routes, notFoundRoute]);
        } else {
          setUserInfo(null);
          setUserPermissions([]);
          setPermissionVersion(null);
          navigate('/login');
        }
      } catch {
        setUserInfo(null);
        setUserPermissions([]);
        setPermissionVersion(null);
        setRetrying(false);
        navigate('/login');
      }
    };

    permission();

    return () => {
      isCancelled = true;
    };
  }, [pathname]);
};

export default useRouterGuard;
